import React from 'react';
import { QuestionKind, QuestionData } from './types';
import { z } from 'zod';
import {
  TextField,
  TextareaField,
  NumberField,
  CheckboxField,
  SelectSingleField,
  SelectMultiField,
  DateField,
  FileField,
  UploadField,
} from './fields';

interface FieldComponentProps {
  question: QuestionData;
  placeholder?: string;
  disabled?: boolean;
}

interface FieldDefinition {
  component: React.ComponentType<FieldComponentProps>;
  createSchema: (question: QuestionData) => z.ZodTypeAny;
  getDefaultValue: (question: QuestionData) => unknown;
  /** Coerces a saved answer into the shape the field edits (optional). */
  normalizeSavedValue?: (value: unknown) => unknown;
}

/**
 * Complete field definitions mapping - each field exports everything we need
 */
const FIELD_DEFINITIONS: Record<QuestionKind, FieldDefinition> = {
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  checkbox: CheckboxField,
  select_single: SelectSingleField,
  select_multi: SelectMultiField,
  date: DateField,
  file: FileField,
  upload: UploadField,
};

const identity = (value: unknown) => value;

export const getFieldDefinition = (kind: QuestionKind): FieldDefinition => {
  const definition = FIELD_DEFINITIONS[kind];
  if (!definition) {
    throw new Error(`No field definition found for type: ${kind}`);
  }
  return definition;
};

export const getFieldComponent = (kind: QuestionKind) => {
  return getFieldDefinition(kind).component;
};

export const createFieldSchema = (question: QuestionData) => {
  const definition = getFieldDefinition(question.kind);
  return {
    schema: definition.createSchema(question),
    defaultValue: definition.getDefaultValue(question),
    normalizeSavedValue: definition.normalizeSavedValue ?? identity,
  };
};
